import React from 'react';
import Icon from '../../../../components/AppIcon';

const ConflictPreview = ({ result, onBack, onNext }) => {
  const { conflicts, companyA, companyB } = result || {};
  const hasConflicts = conflicts?.totalConflicts > 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-text-secondary">Total Conflicts</span>
            <div className={`p-2 rounded-lg ${
              hasConflicts ? 'bg-warning-50' : 'bg-success-50'
            }`}>
              <Icon 
                name={hasConflicts ? 'AlertTriangle' : 'CheckCircle2'} 
                size={20} 
                color={hasConflicts ? '#F59E0B' : '#10B981'} 
              />
            </div>
          </div>
          <p className="text-2xl font-bold text-text-primary">{conflicts?.totalConflicts || 0}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-text-secondary">User Conflicts</span>
            <div className="p-2 bg-error-50 rounded-lg">
              <Icon name="Users" size={20} color="#EF4444" />
            </div>
          </div>
          <p className="text-2xl font-bold text-text-primary">{conflicts?.users?.length || 0}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-text-secondary">Job Conflicts</span>
            <div className="p-2 bg-warning-50 rounded-lg">
              <Icon name="Briefcase" size={20} color="#F59E0B" />
            </div>
          </div>
          <p className="text-2xl font-bold text-text-primary">{conflicts?.jobs?.length || 0}</p>
        </div>
      </div>

      {/* User Conflicts */}
      {conflicts?.users?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-border p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Users" size={24} color="#EF4444" />
            <div>
              <h2 className="text-lg font-semibold text-text-primary">User Conflicts</h2>
              <p className="text-sm text-text-secondary">
                {conflicts?.users?.length} users exist in both companies
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {conflicts?.users?.map((conflict, index) => (
              <div key={index} className="p-4 bg-error-50 border border-error-200 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-medium text-text-primary">{conflict?.email}</p>
                    <p className="text-sm text-text-secondary mt-1">Duplicate user account</p>
                  </div>
                  <span className="px-2 py-1 bg-error-100 text-error-700 text-xs font-medium rounded-full">
                    Conflict
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-error-200">
                  <div>
                    <p className="text-xs font-medium text-text-secondary mb-1">
                      Company A: {companyA?.company_name}
                    </p>
                    <p className="text-sm text-text-primary">
                      Role: <span className="font-medium capitalize">
                        {conflict?.userA?.role?.replace('_', ' ')}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-text-secondary mb-1">
                      Company B: {companyB?.company_name}
                    </p>
                    <p className="text-sm text-text-primary">
                      Role: <span className="font-medium capitalize">
                        {conflict?.userB?.role?.replace('_', ' ')}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Job Conflicts */}
      {conflicts?.jobs?.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-border p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Briefcase" size={24} color="#F59E0B" />
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Job Name Conflicts</h2>
              <p className="text-sm text-text-secondary">
                {conflicts?.jobs?.length} jobs have duplicate names
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {conflicts?.jobs?.map((conflict, index) => (
              <div key={index} className="p-3 bg-warning-50 border border-warning-200 rounded-lg flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Icon name="AlertTriangle" size={18} color="#F59E0B" />
                  <div>
                    <p className="font-medium text-text-primary">{conflict?.jobName}</p>
                    <p className="text-xs text-text-secondary">Duplicate job name detected</p>
                  </div>
                </div>
                <span className="px-2 py-1 bg-warning-100 text-warning-700 text-xs font-medium rounded-full">
                  Warning
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Conflicts Message */}
      {!hasConflicts && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-6 text-center">
          <Icon name="CheckCircle2" size={48} color="#10B981" className="mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-success-800 mb-2">No Conflicts Detected</h3>
          <p className="text-success-700">
            Great news! No user or job conflicts were found between these companies. 
            You can proceed with the merge preview.
          </p>
        </div>
      )}

      {/* Resolution Suggestions */}
      {hasConflicts && (
        <div className="bg-secondary-50 border border-border rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <Icon name="Lightbulb" size={24} color="#3B82F6" className="flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-text-primary mb-2">Conflict Resolution Strategy</h3>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>User conflicts: Keep primary company role, archive secondary account</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Job conflicts: Rename duplicate jobs with company prefix</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-primary mt-1">•</span>
                  <span>Storage: Consolidate files, maintain folder structure</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 border border-border text-text-primary rounded-lg font-medium hover:bg-secondary-50 transition-colors duration-200 flex items-center space-x-2"
        >
          <Icon name="ArrowLeft" size={20} />
          <span>Back</span>
        </button>
        
        <button
          onClick={onNext}
          className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-700 transition-colors duration-200 flex items-center space-x-2"
        >
          <span>View Merge Preview</span>
          <Icon name="ArrowRight" size={20} />
        </button>
      </div>
    </div>
  );
};

export default ConflictPreview;